import React, { useCallback, useEffect, useState } from 'react';
import { Spinner, Alert, Btn, Input, Select, Modal, ConfirmDialog } from '../../components/UI';
import { api, getUser } from '../../utils/api';
import COUNTRIES from '../../data/countries';

// Person card (modal) — full profile from our DB; superadmin can edit details,
// switch Basecamp membership and delete. Opened from the "Where we are" map.

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;
}

// Avatar — profilna slika iz Microsoft Graph, fallback na inicijal.
export function MemberAvatar({ id, name, size = 40 }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    // URLs are cached (and shared) by api.getMemberPhotoURL — never revoke them here.
    let active = true;
    api.getMemberPhotoURL(id).then(u => { if (active) setUrl(u); });
    return () => { active = false; };
  }, [id]);

  const box = {
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    objectFit: 'cover', background: 'var(--ink)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font-display)', fontSize: size * 0.42,
  };
  if (url) return <img src={url} alt={name} style={box} />;
  return <div style={box}>{(name || '?').charAt(0)}</div>;
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: '0.85rem', padding: '4px 0' }}>
      <span style={{ minWidth: 110, color: 'var(--ink-faint)', fontWeight: 600 }}>{label}</span>
      <span style={{ color: 'var(--ink)' }}>{value}</span>
    </div>
  );
}

const textareaStyle = {
  padding: '10px 13px', borderRadius: 'var(--radius-md)', border: '1px solid var(--line)',
  background: 'var(--canvas-white)', color: 'var(--ink)', fontSize: '0.9rem', outline: 'none',
  width: '100%', fontFamily: 'var(--font-body)', resize: 'vertical', minHeight: 90, boxSizing: 'border-box',
};

const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.85rem', textDecoration: 'underline' };
const fieldLbl = { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink-faint)', display: 'block', marginBottom: 4 };
const twoCol = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };

// Operating Countries — chips + a picker to add (multi). One trainer can cover several.
function MultiCountry({ value, onChange }) {
  const list = value || [];
  const add = (c) => { if (c && !list.includes(c)) onChange([...list, c]); };
  const remove = (c) => onChange(list.filter(x => x !== c));
  return (
    <div>
      {list.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          {list.map(c => (
            <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', background: 'var(--canvas-warm)', borderRadius: 99, padding: '2px 6px 2px 10px' }}>
              {c}<button type="button" onClick={() => remove(c)} style={{ ...linkBtn, textDecoration: 'none', color: 'var(--ink-faint)' }}>×</button>
            </span>
          ))}
        </div>
      )}
      <Select value="" onChange={e => { add(e.target.value); }}>
        <option value="">Add a country…</option>
        {COUNTRIES.filter(c => !list.includes(c)).map(c => <option key={c} value={c}>{c}</option>)}
      </Select>
    </div>
  );
}

// Normalizuj LinkedIn URL (admin može uneti bez protokola).
function normUrl(u) {
  if (!u) return null;
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}

// Čitljive oznake rola za People karticu.
const ROLE_LABELS = {
  IP: 'Ice Pilot', BP: 'Business Partner', SH: 'Shareholder',
  ADM: 'Admin', SUPERADMIN: 'Admin', PM: 'Program Manager', CAND: 'Candidate',
};
const roleLabel = (r) => ROLE_LABELS[r] || r;

// Role dropdown — superadmin bira ručno; postavlja stvarnu sistemsku rolu.
// Menja se samo "business" rola; SUPERADMIN / PM / CAND se čuvaju netaknuti.
const ROLE_OPTIONS = [['IP', 'Ice Pilot'], ['BP', 'Business Partner'], ['SH', 'Shareholder'], ['ADM', 'Admin']];
const BUSINESS_ROLES = ['IP', 'BP', 'SH', 'ADM'];

// Status (Ice Pilot lestvica) — pojavljuje se samo kad je Role = Ice Pilot.
const STATUS_OPTIONS = ['HB Qualified', 'Ice Pilot Explorer', 'Ice Pilot Master'];

export default function MemberModal({ member, onClose, onChanged }) {
  const [state, setState] = useState({ loading: true, error: null, details: null });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [bpNames, setBpNames] = useState([]);
  const [busy, setBusy] = useState(false);
  const isSuperadmin = (getUser()?.roles || []).includes('SUPERADMIN');

  const load = useCallback(() => {
    setState(s => ({ ...s, loading: true }));
    api.getMemberDetails(member.id)
      .then(d => setState({ loading: false, error: null, details: d.member }))
      .catch(e => setState({ loading: false, error: e.message, details: null }));
  }, [member.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.getBpNames().then(d => setBpNames(d.names || [])).catch(() => {}); }, []);

  const d = state.details;
  // Trenutna "business" rola (za dropdown) — SUPERADMIN/PM/CAND se ne diraju.
  const currentRole = d ? (d.roles || []).find(r => BUSINESS_ROLES.includes(r)) || '' : '';

  function startEdit() {
    setForm({
      role: currentRole,
      icePilotStatus: d.icePilotStatus || '',
      startDate: d.startDate ? new Date(d.startDate).toISOString().slice(0, 10) : '',
      businessPartnerCountry: d.businessPartnerCountry || '', businessPartnerName: d.businessPartnerName || '',
      operatingCountries: Array.isArray(d.operatingCountries) ? d.operatingCountries : [],
      city: d.city || '', phone: d.phone || '', linkedInUrl: d.linkedInUrl || '',
      cvText: d.cvText || '',
    });
    setCvFile(null);
    setEditing(true);
  }
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    setBusy(true);
    try {
      // Status važi samo za Ice Pilot; ostalo ga briše.
      const isIP = form.role === 'IP';
      const profile = {
        icePilotStatus: isIP ? form.icePilotStatus : '',
        startDate: form.startDate,
        businessPartnerCountry: form.businessPartnerCountry,
        businessPartnerName: form.businessPartnerName,
        operatingCountries: form.operatingCountries,
        city: form.city, phone: form.phone, linkedInUrl: form.linkedInUrl,
        cvText: form.cvText,
      };
      await api.admin.updateProfile(member.id, profile);
      // Role: superadmin ručno menja stvarnu rolu; čuvamo SUPERADMIN/PM/CAND.
      if (form.role !== currentRole) {
        const preserved = (d.roles || []).filter(r => !BUSINESS_ROLES.includes(r));
        const newRoles = [...new Set([...(form.role ? [form.role] : []), ...preserved])];
        await api.admin.updateRoles(member.id, newRoles);
      }
      if (cvFile) await api.admin.uploadCv(member.id, cvFile);
      setCvFile(null);
      setEditing(false);
      load();
      onChanged && onChanged();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  async function removeCv() {
    if (!window.confirm('Remove the attached CV file?')) return;
    setBusy(true);
    try { await api.admin.deleteCv(member.id); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  function downloadCv() {
    api.downloadMemberCv(member.id, d.cvFileName || `${member.name}-CV`).catch(e => alert(e.message));
  }

  const [memberError, setMemberError] = useState(null);
  const membership = useMembershipToggle({
    onChanged: () => { setMemberError(null); load(); onChanged && onChanged(); },
    onError: setMemberError,
  });

  async function remove() {
    if (!window.confirm(`Permanently delete ${member.name}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.admin.deleteUser(member.id);
      onChanged && onChanged();
      onClose();
    } catch (e) {
      // 409: korisnik ima povezane zapise → server predlaže deaktivaciju.
      alert(e.message);
    } finally { setBusy(false); }
  }

  return (
    <>
    <Modal open onClose={onClose} title={member.name}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <MemberAvatar id={member.id} name={member.name} size={64} />
        <div>
          <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{member.name}</div>
          {d && !editing && (d.roles || []).length > 0 && (
            <div style={{ fontSize: '0.85rem', color: 'var(--ink-soft)' }}>{(d.roles || []).map(roleLabel).join(', ')}</div>
          )}
        </div>
      </div>

      {state.loading && <Loading />}
      {state.error && <Alert type="error">{state.error}</Alert>}

      {d && !editing && (
        <div>
          <DetailRow label="Role" value={d.roles.length ? d.roles.map(roleLabel).join(', ') : null} />
          <DetailRow label="Status" value={d.icePilotStatus} />
          <DetailRow label="Business Partner" value={[d.businessPartnerCountry, d.businessPartnerName].filter(Boolean).join(' · ')} />
          <DetailRow label="HB Join Date" value={d.startDate ? new Date(d.startDate).toLocaleDateString() : null} />
          <DetailRow label="Operating Countries" value={(d.operatingCountries || []).join(', ')} />
          <DetailRow label="City" value={d.city} />
          <DetailRow label="Email" value={d.email && <a href={`mailto:${d.email}`} style={{ color: 'var(--ink)' }}>{d.email}</a>} />
          <DetailRow label="Phone" value={d.phone} />
          <DetailRow label="LinkedIn" value={d.linkedInUrl && <a href={normUrl(d.linkedInUrl)} target="_blank" rel="noreferrer" style={{ color: 'var(--ink)' }}>View profile ↗</a>} />
          <DetailRow label="CV file" value={d.hasCvFile && (
            <button onClick={downloadCv} style={{ ...linkBtn, color: 'var(--ink)' }}>
              Download{d.cvFileName ? ` (${d.cvFileName})` : ''}
            </button>
          )} />
          {d.cvText && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: 6 }}>CV (text)</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--ink)', whiteSpace: 'pre-wrap', lineHeight: 1.5, background: 'var(--canvas-warm)', padding: '12px 14px', borderRadius: 'var(--radius-md)' }}>{d.cvText}</div>
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 12 }}>
            {d.roles.map(r => (
              <span key={r} style={{ fontSize: '0.65rem', padding: '1px 8px', borderRadius: '99px', border: '1px solid var(--ink-soft)', color: 'var(--ink-soft)', fontWeight: 600 }}>{roleLabel(r)}</span>
            ))}
            {d.funcs.map(f => (
              <span key={f} style={{ fontSize: '0.65rem', padding: '1px 8px', borderRadius: '99px', border: '1px dashed var(--ink-faint)', color: 'var(--ink-faint)' }}>{f}</span>
            ))}
          </div>

          {isSuperadmin && memberError && <div style={{ marginTop: 14 }}><Alert type="error">{memberError}</Alert></div>}
          {isSuperadmin && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 20, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
              <MembershipSwitch on={d.isMember} busy={busy || membership.busyId === member.id} onToggle={() => membership.toggle({ ...member, isMember: d.isMember })} />
              <Btn size="sm" onClick={startEdit}>Edit details</Btn>
              <Btn size="sm" variant="outline" loading={busy} onClick={remove} style={{ color: 'var(--danger, #c0392b)', borderColor: 'var(--danger, #c0392b)' }}>Delete person</Btn>
            </div>
          )}
        </div>
      )}

      {d && editing && form && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Role (left) + Status (right, only when Ice Pilot) */}
          <div style={twoCol}>
            <div>
              <label style={fieldLbl}>Role</label>
              <Select value={form.role} onChange={e => setF('role', e.target.value)}>
                <option value="">Role…</option>
                {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </div>
            <div>
              {form.role === 'IP' && (
                <>
                  <label style={fieldLbl}>Status</label>
                  <Select value={form.icePilotStatus} onChange={e => setF('icePilotStatus', e.target.value)}>
                    <option value="">Status…</option>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </>
              )}
            </div>
          </div>

          {/* HB Join Date */}
          <div style={{ maxWidth: 240 }}>
            <label style={fieldLbl}>HB Join Date</label>
            <Input type="date" value={form.startDate} onChange={e => setF('startDate', e.target.value)} />
          </div>

          {/* Business Partner — Country (left) + Name (right) */}
          <div style={twoCol}>
            <div>
              <label style={fieldLbl}>Business Partner — Country</label>
              <Select value={form.businessPartnerCountry} onChange={e => setF('businessPartnerCountry', e.target.value)}>
                <option value="">Country…</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </div>
            <div>
              <label style={fieldLbl}>Business Partner — Name</label>
              <Select value={form.businessPartnerName} onChange={e => setF('businessPartnerName', e.target.value)}>
                <option value="">Partner…</option>
                {/* keep a previously-saved value even if it's not in the group list */}
                {form.businessPartnerName && !bpNames.includes(form.businessPartnerName) &&
                  <option value={form.businessPartnerName}>{form.businessPartnerName}</option>}
                {bpNames.map(n => <option key={n} value={n}>{n}</option>)}
              </Select>
            </div>
          </div>

          {/* Operating Countries — one trainer can cover several */}
          <div>
            <label style={fieldLbl}>Operating Countries</label>
            <MultiCountry value={form.operatingCountries} onChange={v => setF('operatingCountries', v)} />
          </div>

          {/* City (left) + Phone (right) */}
          <div style={twoCol}>
            <div><label style={fieldLbl}>City</label><Input value={form.city} onChange={e => setF('city', e.target.value)} /></div>
            <div><label style={fieldLbl}>Phone</label><Input value={form.phone} onChange={e => setF('phone', e.target.value)} /></div>
          </div>

          {/* LinkedIn */}
          <div><label style={fieldLbl}>LinkedIn URL</label><Input value={form.linkedInUrl} onChange={e => setF('linkedInUrl', e.target.value)} /></div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-faint)', margin: '4px 0 6px' }}>CV file</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <input type="file" onChange={e => setCvFile(e.target.files?.[0] || null)} style={{ fontSize: '0.85rem' }} />
              {d.hasCvFile && !cvFile && (
                <span style={{ fontSize: '0.8rem', color: 'var(--ink-soft)' }}>
                  Current: {d.cvFileName || 'file'} · <button onClick={removeCv} style={{ ...linkBtn, color: 'var(--danger, #c0392b)', fontSize: '0.8rem' }}>remove</button>
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.74rem', color: 'var(--ink-faint)', marginTop: 4 }}>A downloadable file (PDF, DOCX…), max 15 MB. Later versions will break the CV into searchable fields.</div>
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-faint)', margin: '4px 0 6px' }}>CV (text)</div>
            <textarea placeholder="Paste CV / bio text…" value={form.cvText} onChange={e => setF('cvText', e.target.value)} style={textareaStyle} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <Btn size="sm" loading={busy} onClick={save}>Save</Btn>
            <Btn size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Btn>
          </div>
        </div>
      )}
    </Modal>
    {membership.dialog}
    </>
  );
}

// Basecamp membership on/off (superadmin). Off = hidden from Basecamp + no sign-in;
// the Microsoft account is never touched.
export function MembershipSwitch({ on, busy, onToggle, compact = false }) {
  return (
    <label title={on ? 'Member of Basecamp — click to remove' : 'Not in Basecamp — click to add back'}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: busy ? 'wait' : 'pointer', fontSize: compact ? '0.72rem' : '0.82rem', color: 'var(--ink-soft)', marginRight: compact ? 0 : 'auto', flexShrink: 0 }}
      onClick={e => e.stopPropagation()}>
      <input type="checkbox" role="switch" aria-checked={!!on} checked={!!on} disabled={busy} onChange={onToggle}
        style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }} />
      <span aria-hidden="true" style={{ position: 'relative', width: 28, height: 16, borderRadius: 99, background: on ? 'var(--ink)' : 'var(--canvas-warm)', border: '1px solid var(--ink-faint)', transition: 'background 0.15s' }}>
        <span style={{ position: 'absolute', top: 1, left: on ? 13 : 1, width: 12, height: 12, borderRadius: '50%', background: on ? '#fff' : 'var(--ink-faint)', transition: 'left 0.15s' }} />
      </span>
      {!compact && <span>{on ? 'Basecamp member' : 'Not in Basecamp'}</span>}
    </label>
  );
}

// Membership on/off with a site-styled confirm for removal (adding back needs no confirm).
// Usage: const m = useMembershipToggle({ onChanged(person, isMember), onError(msg) });
//        m.toggle(person) from a switch, render {m.dialog} once; m.busyId = person being saved.
export function useMembershipToggle({ onChanged, onError } = {}) {
  const [pending, setPending] = useState(null); // person waiting for "Remove" confirm
  const [busyId, setBusyId] = useState(null);
  const [dialogError, setDialogError] = useState(null);

  async function apply(person, next) {
    setBusyId(person.id);
    try {
      await api.admin.setMembership(person.id, next);
      setPending(null);
      onChanged && onChanged(person, next);
    } catch (e) {
      if (next) onError && onError(e.message); else setDialogError(e.message);
    } finally { setBusyId(null); }
  }

  function toggle(person) {
    if (person.isMember === false) { apply(person, true); return; }
    setDialogError(null);
    setPending(person);
  }

  const name = pending ? (pending.name || pending.email || 'This person') : '';
  const dialog = (
    <ConfirmDialog
      open={!!pending}
      title="Remove from Basecamp?"
      confirmLabel="Remove from Basecamp"
      danger
      busy={!!pending && busyId === pending.id}
      error={dialogError}
      onCancel={() => setPending(null)}
      onConfirm={() => apply(pending, false)}
    >
      <p style={{ margin: '0 0 10px' }}><b style={{ color: 'var(--ink)' }}>{name}</b> will no longer be able to sign in to Basecamp and will be hidden from it.</p>
      <p style={{ margin: 0 }}>Their Microsoft account is not touched. You can add them back any time with the same switch.</p>
    </ConfirmDialog>
  );

  return { toggle, dialog, busyId };
}
